import app from 'flarum/common/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Tooltip from 'flarum/common/components/Tooltip';
import extractText from 'flarum/common/utils/extractText';
import { buttonsFor, DEFAULT_TOOLBAR, ALIGN_ACTIONS, TABLE_ACTIONS } from '../toolbarButtons';
import { registeredButtons } from '../registry';
import { insertVideo } from '../video/insert';
import { anyVideoFromUrl, videoBrand, videoEmbedsEnabled, videoFromUrl, watchUrl } from '../video/providers';

/**
 * A registered button carries its own full translation key; Scribe's own
 * carry a suffix under Scribe's namespace.
 */
function buttonLabel(b) {
  return app.translator.trans(b.translationKey ?? `ernestdefoe-scribe.lib.buttons.${b.label}`);
}

/**
 * A TipTap shortcut ("Mod-Shift-b") the way the reader's own keyboard writes
 * it: "⌘⇧B" on a Mac, "Ctrl+Shift+B" everywhere else.
 */
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
export function formatShortcut(shortcut) {
  const parts = shortcut.split('-');
  const key = parts.pop();
  const keyLabel = key.length === 1 ? key.toUpperCase() : key;
  if (IS_MAC) {
    const symbols = {
      Mod: '⌘',
      Ctrl: '⌃',
      Alt: '⌥',
      Shift: '⇧'
    };
    return parts.map(p => symbols[p] ?? p).join('') + keyLabel;
  }
  const names = {
    Mod: 'Ctrl',
    Ctrl: 'Ctrl',
    Alt: 'Alt',
    Shift: 'Shift'
  };
  return [...parts.map(p => names[p] ?? p), keyLabel].join('+');
}

/** The tooltip: the button's name, then its shortcut when it has one. */
function tooltipText(b) {
  const label = buttonLabel(b);
  return b.shortcut ? `${label} (${formatShortcut(b.shortcut)})` : label;
}
/** Colours offered by the swatch row. Deliberately few — a forum post is not a design tool. */
const SWATCHES = ['#d83e3e', '#e8590c', '#f08c00', '#2f9e44', '#1971c2', '#6741d9', '#868e96'];

/** Mark name + attribute each colour prompt writes to. */
const COLOR_PROMPT_TARGET = {
  color: {
    mark: 'scribeColor',
    clearKey: 'clear_colour'
  },
  highlight: {
    mark: 'highlight',
    clearKey: 'clear_highlight'
  }
};
const HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * `<input type="color">` only ever accepts a full `#rrrrgg`-shaped 6-digit
 * hex — an empty field, a 3-digit shorthand, or free text mid-typing would
 * make the browser silently reset it to black. Widen 3-digit shorthand and
 * fall back to black for anything else not yet valid, purely for what the
 * picker swatch itself shows; the text field stays the source of truth.
 */
function toPickerHex(value) {
  const v = value.trim();
  if (/^#[0-9a-f]{6}$/i.test(v)) return v;
  const m = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(v);
  return m ? `#${m[1]}${m[1]}${m[2]}${m[2]}${m[3]}${m[3]}` : '#000000';
}

/** Preset defaults for the Info prompt's three colour fields. */
const INFO_DEFAULTS = {
  font: '#1E2019',
  bg: '#B8D3D1',
  border: '#B8D3D1'
};
export default class ScribeToolbar extends Component {
  prompt = null;
  value = '';
  infoFont = (() => INFO_DEFAULTS.font)();
  infoBg = (() => INFO_DEFAULTS.bg)();
  infoBorder = (() => INFO_DEFAULTS.border)();
  /** No lower bound — a 1×1 table is a valid table, not an error case. */
  tableRows = 3;
  tableCols = 3;
  /**
   * 🚨 Not a custom picker — just a text field the OS/browser's own emoji
   * keyboard (Win+. / Cmd+Ctrl+Space / mobile emoji key) can type into.
   * Building a picker here would duplicate pianotell-flamoji's, which isn't
   * wired into TipTap at all (see the forum-flarum-2-rc-upgrades follow-up).
   * Prepended into the title once, on Apply — not itself round-tripped back
   * out when reopening an existing info box for editing.
   */
  infoEmoji = '';
  /** Why the video form refused the last link, shown under it. */
  videoError = '';
  view() {
    const editor = this.attrs.editor;
    if (!editor) return null;

    /*
     * An admin who has never touched the setting gets the defaults PLUS
     * whatever other extensions have registered.
     *
     * 🚨 Only on the default path. Once the toolbar has been arranged by hand
     * that arrangement is an explicit decision, and an extension installed
     * later must not silently push a button into it. The admin finds it in the
     * builder's palette instead — which is why anything registering a button
     * still has to work without it (see common/registry.ts).
     */
    // Ctrl/⌘+K, bound in the editor (see ScribeLinkShortcut), opens the same
    // link form as the button. The toolbar owns that form, so it hands the
    // editor a way in.
    const storage = editor.storage.scribeLinkShortcut;
    if (storage) {
      storage.open = () => {
        this.openPrompt('link', editor);
        this.attrs.onChange();
      };
    }

    // The video node's own "Replace" button opens the video form, the same way.
    const videoStorage = editor.storage.scribeVideo;
    if (videoStorage) {
      videoStorage.open = () => {
        this.prompt = null;
        this.openPrompt('video', editor);
        this.attrs.onChange();
      };
    }
    const saved = app.forum.attribute('scribeToolbar');
    const configured = saved ?? [...DEFAULT_TOOLBAR, ...registeredButtons().map(b => b.key)];
    return m("div", {
      className: "Scribe-toolbarWrap"
    }, m("div", {
      className: "Scribe-toolbar",
      role: "toolbar",
      "aria-label": app.translator.trans('ernestdefoe-scribe.forum.composer.toolbar_label')
    }, buttonsFor(configured)
    // Embeds switched off in the AdminCP: no button that would make one.
    .filter(b => b.key !== 'video' || videoEmbedsEnabled()).map(b => this.button(b, editor))), this.prompt && this.promptRow(editor));
  }
  button(b, editor) {
    const label = buttonLabel(b);
    const active = b.active?.(editor) ?? false;
    return m(Tooltip, {
      text: tooltipText(b)
    }, Button.component({
      className: 'Button Button--icon Button--link Scribe-toolbarButton' + (active ? ' is-active' : ''),
      icon: b.icon,
      // The pressed state is what tells a screen-reader user the cursor is
      // inside bold text, which sighted users read off the highlight.
      'aria-pressed': active ? 'true' : 'false',
      'aria-label': label,
      // Screen readers announce the shortcut the tooltip shows.
      'aria-keyshortcuts': b.shortcut ? b.shortcut.replace('Mod', IS_MAC ? 'Meta' : 'Control').replace(/-/g, '+') : undefined,
      'data-badge': b.badge,
      disabled: b.enabled ? !b.enabled(editor) : false,
      onclick: () => {
        if (b.prompt) {
          this.openPrompt(b.prompt, editor);
        } else {
          b.run?.(editor);
        }
        this.attrs.onChange();
      }
    }));
  }

  /**
   * A button inside a group dropdown (align, table) — same look as a normal
   * toolbar button, but deliberately does NOT close the dropdown after
   * running. Applying an align option or adding three rows in a row means
   * clicking this menu repeatedly; forcing a re-open after every click was
   * the opposite of what grouping them was for. The dropdown only closes
   * when its own toolbar button is toggled again (see `openPrompt`).
   */
  menuItem(b, editor) {
    const label = buttonLabel(b);
    const active = b.active?.(editor) ?? false;
    return m(Tooltip, {
      text: tooltipText(b)
    }, Button.component({
      className: 'Button Button--icon Button--link Scribe-toolbarButton' + (active ? ' is-active' : ''),
      icon: b.icon,
      'aria-pressed': active ? 'true' : 'false',
      'aria-label': label,
      // Screen readers announce the shortcut the tooltip shows.
      'aria-keyshortcuts': b.shortcut ? b.shortcut.replace('Mod', IS_MAC ? 'Meta' : 'Control').replace(/-/g, '+') : undefined,
      'data-badge': b.badge,
      disabled: b.enabled ? !b.enabled(editor) : false,
      onclick: () => {
        b.run?.(editor);
        this.attrs.onChange();
      }
    }));
  }
  openPrompt(kind, editor) {
    // Reopening the same prompt closes it, so the button toggles.
    this.prompt = this.prompt === kind ? null : kind;
    this.videoError = '';
    if (kind === 'link') this.value = editor.getAttributes('link').href ?? editor.getAttributes('image').href ?? '';else if (kind === 'video') {
      const attrs = editor.getAttributes('scribeVideo');
      this.value = attrs.provider ? watchUrl(attrs) ?? '' : '';
    } else if (kind === 'spoiler') this.value = editor.getAttributes('scribeSpoiler').label ?? '';else if (kind === 'info') {
      const attrs = editor.getAttributes('scribeInfo');
      this.value = attrs.label ?? '';
      this.infoEmoji = '';
      this.infoFont = attrs.font ?? INFO_DEFAULTS.font;
      this.infoBg = attrs.bg ?? INFO_DEFAULTS.bg;
      this.infoBorder = attrs.border ?? INFO_DEFAULTS.border;
    } else if (kind === 'table') {
      this.tableRows = 3;
      this.tableCols = 3;
    } else this.value = '';
  }

  /**
   * 🚨 These three buttons open a form instead of toggling, and until this
   * existed they were wired to a no-op — present, labelled, styled, and doing
   * nothing. A control that looks like a feature and silently does nothing is
   * worse than one that is missing.
   */
  promptRow(editor) {
    var _this = this;
    const kind = this.prompt;
    const close = () => {
      this.prompt = null;
      this.value = '';
      editor.commands.focus();
      this.attrs.onChange();
    };
    if (kind === 'color' || kind === 'highlight') {
      const {
        mark,
        clearKey
      } = COLOR_PROMPT_TARGET[kind];
      const applyColor = c => {
        editor.chain().focus().setMark(mark, {
          color: c
        }).run();
        close();
      };
      const hexValid = HEX_PATTERN.test(this.value.trim());
      return m("div", {
        className: "Scribe-prompt Scribe-prompt--color"
      }, SWATCHES.map(c => m("button", {
        type: "button",
        className: "Scribe-swatch",
        style: {
          background: c
        },
        "aria-label": c,
        title: c,
        onclick: () => applyColor(c)
      })), m("input", {
        type: "color",
        className: "Scribe-colorPicker",
        "aria-label": app.translator.trans('ernestdefoe-scribe.forum.composer.pick_colour'),
        value: toPickerHex(this.value),
        oninput: e => {
          this.value = e.target.value;
          this.attrs.onChange();
        }
      }), m("input", {
        className: "FormControl Scribe-promptInput Scribe-promptHex",
        type: "text",
        placeholder: "#f08c00",
        value: this.value,
        oninput: e => {
          this.value = e.target.value;
          // Re-render so the Apply button's disabled state (derived from
          // this.value) updates as the user types, not just on the next
          // unrelated redraw.
          this.attrs.onChange();
        },
        onkeydown: e => {
          if (e.key === 'Enter' && HEX_PATTERN.test(this.value.trim())) {
            e.preventDefault();
            applyColor(this.value.trim());
          } else if (e.key === 'Escape') {
            close();
          }
        }
      }), Button.component({
        className: 'Button Button--primary Scribe-promptApply',
        disabled: !hexValid,
        onclick: () => applyColor(this.value.trim())
      }, app.translator.trans('ernestdefoe-scribe.forum.composer.apply')), Button.component({
        className: 'Button Button--link Scribe-promptClear',
        onclick: () => {
          editor.chain().focus().unsetMark(mark).run();
          close();
        }
      }, app.translator.trans(`ernestdefoe-scribe.forum.composer.${clearKey}`)));
    }
    if (kind === 'alignMenu') {
      return m("div", {
        className: "Scribe-prompt Scribe-prompt--menu"
      }, ALIGN_ACTIONS.map(b => this.menuItem(b, editor)));
    }
    if (kind === 'tableMenu') {
      // Reuses the existing prompt machinery: this item has its own
      // `prompt: 'table'`, so `this.button()` opens the size-picker below
      // exactly the way any other prompt button does — no special-casing.
      const insertTable = {
        key: 'insertTable',
        icon: 'fas fa-table',
        label: 'table',
        prompt: 'table'
      };
      return m("div", {
        className: "Scribe-prompt Scribe-prompt--menu"
      }, this.button(insertTable, editor), TABLE_ACTIONS.map(b => this.menuItem(b, editor)));
    }
    if (kind === 'table') {
      const clamp = n => Math.max(1, Math.min(50, Math.floor(n) || 1));
      const apply = () => {
        editor.chain().focus().insertTable({
          rows: clamp(this.tableRows),
          cols: clamp(this.tableCols),
          withHeaderRow: true
        }).run();
        close();
      };
      const sizeField = (value, set, label) => m("input", {
        className: "FormControl Scribe-promptInput Scribe-promptNumber",
        type: "number",
        min: 1,
        max: 50,
        "aria-label": label,
        value: value,
        oninput: e => set(e.target.valueAsNumber)
      });
      return m("div", {
        className: "Scribe-prompt"
      }, sizeField(this.tableRows, n => this.tableRows = n, 'rows'), m("span", {
        "aria-hidden": "true"
      }, "\xD7"), sizeField(this.tableCols, n => this.tableCols = n, 'columns'), Button.component({
        className: 'Button Button--primary Scribe-promptApply',
        onclick: apply
      }, app.translator.trans('ernestdefoe-scribe.forum.composer.apply')));
    }
    if (kind === 'spoiler') {
      const apply = () => {
        editor.chain().focus().wrapIn('scribeSpoiler', {
          label: this.value.trim()
        }).run();
        close();
      };
      return m("div", {
        className: "Scribe-prompt"
      }, m("input", {
        className: "FormControl Scribe-promptInput",
        type: "text",
        placeholder: app.translator.trans('ernestdefoe-scribe.forum.composer.spoiler_title_placeholder'),
        value: this.value,
        oninput: e => {
          this.value = e.target.value;
        },
        onkeydown: e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            apply();
          } else if (e.key === 'Escape') {
            close();
          }
        },
        oncreate: v => v.dom.focus()
      }), Button.component({
        className: 'Button Button--primary Scribe-promptApply',
        onclick: apply
      }, app.translator.trans('ernestdefoe-scribe.forum.composer.apply')));
    }
    if (kind === 'info') {
      const apply = () => {
        const emoji = this.infoEmoji.trim();
        const label = emoji ? `${emoji} ${this.value.trim()}`.trim() : this.value.trim();
        editor.chain().focus().wrapIn('scribeInfo', {
          label,
          font: HEX_PATTERN.test(this.infoFont) ? this.infoFont : null,
          bg: HEX_PATTERN.test(this.infoBg) ? this.infoBg : null,
          border: HEX_PATTERN.test(this.infoBorder) ? this.infoBorder : null
        }).run();
        close();
      };
      const field = function (value, set, placeholder, label, withPicker) {
        if (withPicker === void 0) {
          withPicker = false;
        }
        return m("span", {
          className: "Scribe-colorField"
        }, withPicker && m("input", {
          type: "color",
          className: "Scribe-colorPicker",
          "aria-label": `pick ${label}`,
          value: toPickerHex(value),
          oninput: e => {
            set(e.target.value);
            _this.attrs.onChange();
          }
        }), m("input", {
          className: "FormControl Scribe-promptInput",
          type: "text",
          "aria-label": label,
          placeholder: placeholder,
          value: value,
          oninput: e => set(e.target.value)
        }));
      };
      return m("div", {
        className: "Scribe-prompt Scribe-prompt--info"
      }, m("input", {
        className: "FormControl Scribe-promptInput Scribe-promptEmoji",
        type: "text",
        "aria-label": app.translator.trans('ernestdefoe-scribe.forum.composer.emoji_label'),
        placeholder: "\uD83D\uDE42",
        value: this.infoEmoji,
        oninput: e => this.infoEmoji = e.target.value
      }), field(this.value, v => this.value = v, app.translator.trans('ernestdefoe-scribe.forum.composer.info_title_placeholder'), app.translator.trans('ernestdefoe-scribe.forum.composer.info_title_placeholder')), field(this.infoFont, v => this.infoFont = v, INFO_DEFAULTS.font, 'font colour', true), field(this.infoBg, v => this.infoBg = v, INFO_DEFAULTS.bg, 'background colour', true), field(this.infoBorder, v => this.infoBorder = v, INFO_DEFAULTS.border, 'border colour', true), Button.component({
        className: 'Button Button--primary Scribe-promptApply',
        onclick: apply
      }, app.translator.trans('ernestdefoe-scribe.forum.composer.apply')));
    }
    if (kind === 'video') return this.videoPrompt(editor, close);
    const placeholder = app.translator.trans(`ernestdefoe-scribe.forum.composer.${kind === 'link' ? 'link_placeholder' : 'image_placeholder'}`);
    const apply = () => {
      const url = this.value.trim();
      if (!url) return close();
      if (kind === 'link') {
        editor.chain().focus().extendMarkRange('link').setLink({
          href: url
        }).setImageLink(url).run();
      } else {
        editor.chain().focus().setImage({
          src: url
        }).run();
      }
      close();
    };
    return m("div", {
      className: "Scribe-prompt"
    }, m("input", {
      className: "FormControl Scribe-promptInput",
      type: "url",
      placeholder: placeholder,
      value: this.value,
      oninput: e => {
        this.value = e.target.value;
      },
      onkeydown: e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          apply();
        } else if (e.key === 'Escape') {
          close();
        }
      },
      oncreate: v => v.dom.focus()
    }), Button.component({
      className: 'Button Button--primary Scribe-promptApply',
      onclick: apply
    }, app.translator.trans('ernestdefoe-scribe.forum.composer.apply')), kind === 'link' && (editor.isActive('link') || !!editor.getAttributes('image').href) && Button.component({
      className: 'Button Button--link',
      onclick: () => {
        editor.chain().focus().extendMarkRange('link').unsetLink().setImageLink(null).run();
        close();
      }
    }, app.translator.trans('ernestdefoe-scribe.forum.composer.remove_link')));
  }

  /**
   * The Video button's form: paste a link, get an embed — or a reason why not.
   *
   * 🚨 It says WHY a link was refused. "Nothing happened" after pasting a link
   * to a provider the admin switched off reads as Scribe being broken.
   */
  videoPrompt(editor, close) {
    const apply = () => {
      const url = this.value.trim();
      if (!url) return close();
      const ref = videoFromUrl(url);
      if (!ref) {
        const known = anyVideoFromUrl(url);
        this.videoError = extractText(app.translator.trans(known ? 'ernestdefoe-scribe.forum.video.provider_off' : 'ernestdefoe-scribe.forum.video.unsupported', known ? {
          provider: videoBrand(known.provider)
        } : {}));
        this.attrs.onChange();
        return;
      }

      // Replacing a video keeps the caption its author already wrote.
      const selected = editor.state.selection.node;
      const caption = selected?.type?.name === 'scribeVideo' ? selected.textContent : '';
      insertVideo(editor, {
        ...ref,
        caption
      });
      close();
    };
    return m("div", {
      className: "Scribe-prompt Scribe-prompt--video"
    }, m("input", {
      className: "FormControl Scribe-promptInput",
      type: "url",
      placeholder: app.translator.trans('ernestdefoe-scribe.forum.video.placeholder'),
      "aria-label": app.translator.trans('ernestdefoe-scribe.lib.buttons.video'),
      "aria-invalid": this.videoError ? 'true' : undefined,
      value: this.value,
      oninput: e => {
        this.value = e.target.value;
        if (this.videoError) {
          this.videoError = '';
          this.attrs.onChange();
        }
      },
      onkeydown: e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          apply();
        } else if (e.key === 'Escape') {
          close();
        }
      },
      oncreate: v => v.dom.focus()
    }), Button.component({
      className: 'Button Button--primary Scribe-promptApply',
      onclick: apply
    }, app.translator.trans('ernestdefoe-scribe.forum.composer.apply')), this.videoError && m("p", {
      className: "Scribe-promptError",
      role: "alert"
    }, this.videoError));
  }
}
flarum.reg.add('ernestdefoe-scribe', 'common/components/ScribeToolbar', ScribeToolbar);